/**
 * Admin Route Guard - Phase 11 + BUG-001 Fix
 * 
 * Protects admin routes with authentication and permission checks.
 * Shows Not Authorized page when permission is denied.
 * 
 * BUG-001 FIX: Prevents admin UI flicker by:
 * 1. Using useLayoutEffect for synchronous auth check
 * 2. Not rendering children until BOTH auth AND permissions are fully resolved
 * 3. Showing a neutral loading screen (not admin shell) during initialization
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useState, useLayoutEffect } from 'react';
import { useAdmin } from '@/context/AdminContext';
import { usePermissions } from '@/context/PermissionsContext';
import { ROUTE_PERMISSIONS } from '@/constants/permissions';
import NotAuthorizedPage from '@/pages/admin/NotAuthorizedPage';

// Neutral loading screen - NOT the admin layout
function AdminLoadingScreen() {
  return (
    <div 
      className="min-h-screen bg-[#0a0a0f] flex items-center justify-center"
      data-testid="admin-loading-screen"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-3 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
        <span className="text-sm text-gray-500">Verifying access...</span>
      </div>
    </div>
  );
}

export function AdminRouteGuard({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAdmin();
  const { canAny, isLoading: permLoading } = usePermissions();
  const location = useLocation();
  
  // Track if we've completed initial auth check
  const [isInitialized, setIsInitialized] = useState(false);

  // Use useLayoutEffect to prevent any flash before auth state is determined
  useLayoutEffect(() => {
    // Only mark as initialized once both auth and permissions have loaded
    if (!authLoading && !permLoading) {
      setIsInitialized(true);
    }
  }, [authLoading, permLoading]);

  // CRITICAL: Show neutral loading screen while initializing
  // This prevents any admin UI from flashing before auth is confirmed
  if (!isInitialized || authLoading || permLoading) {
    return <AdminLoadingScreen />;
  }

  // Redirect to the correct role-scoped login URL if not authenticated.
  // At this point we KNOW auth state is determined, so redirect is safe.
  if (!isAuthenticated) {
    // Superadmin routes → /superadmin/login
    // Vendor panel (/admin/*) → /admin/login
    // Anything else → generic /login
    const p = location.pathname || "";
    let loginPath = "/login";
    if (p.startsWith("/superadmin")) {
      loginPath = "/superadmin/login";
    } else if (p.startsWith("/admin")) {
      loginPath = "/admin/login";
    }
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check route permissions
  const routePerms = ROUTE_PERMISSIONS[location.pathname];
  if (routePerms && routePerms.length > 0) {
    const hasAccess = canAny(routePerms);
    if (!hasAccess) {
      return <NotAuthorizedPage requiredPermissions={routePerms} />;
    }
  }

  // Only render admin layout AFTER confirmed authenticated and permitted
  return children;
}

export default AdminRouteGuard;
